/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import {
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors } from '../../../shared/theme/colors'
import { spacing } from '../../../shared/theme/spacing';
import { ticketColors } from '../../../shared/theme/ticketColors';
import { WEB_SIDEBAR_BREAKPOINT } from '../../../shared/layout/breakpoints';
import { AuthScreenFrame } from '../../../shell/brand/AuthScreenFrame';
import { OnboardingBrandRail } from './OnboardingBrandRail';
import { welcomeRailSteps } from './onboardingRailCopy';

type Props = {
  onStart: () => void;
  onAwaitingInvite?: () => void | Promise<void>;
  stepCount: number;
};

export function OnboardingWelcomeScreen({ onStart, onAwaitingInvite, stepCount }: Props) {
  void stepCount;
  const { width } = useWindowDimensions();
  const useSplit = Platform.OS === 'web' && width >= WEB_SIDEBAR_BREAKPOINT;

  const form = (
    <View style={[styles.form, useSplit ? styles.formWide : styles.formMobile]}>
      {!useSplit ? (
        <View style={styles.logoRow} accessibilityRole="header">
          <View style={styles.logoMark} accessibilityElementsHidden>
            <Text style={styles.logoMarkLetter}>V</Text>
          </View>
          <Text style={styles.logoWordmark}>Vuelto</Text>
        </View>
      ) : null}

      <View style={styles.head}>
        <Text style={styles.title}>Armá tu local</Text>
        <Text style={styles.sub}>
          {useSplit
            ? 'En unos minutos tenés el local listo.'
            : 'En unos minutos tenés el local listo. Elegí rubro, poné el nombre y arrancás.'}
        </Text>
      </View>

      <TouchableOpacity
        style={styles.primaryBtn}
        onPress={onStart}
        accessibilityRole="button"
        accessibilityLabel="Empezar"
        testID="onboarding.start"
      >
        <Text style={styles.primaryLabel}>Empezar</Text>
        <Ionicons name="arrow-forward-outline" size={18} color="#FFFFFF" />
      </TouchableOpacity>

      {onAwaitingInvite ? (
        <TouchableOpacity
          onPress={() => void onAwaitingInvite()}
          accessibilityRole="button"
          accessibilityLabel="Me van a invitar a un local"
          testID="onboarding.awaiting-invite"
          style={styles.awaitingWrap}
        >
          <Text style={styles.awaitingLink}>Me van a invitar a un local</Text>
        </TouchableOpacity>
      ) : null}
    </View>
  );

  return (
    <AuthScreenFrame showWatermark={false} backgroundColor={useSplit ? '#FFFFFF' : colors.background}>
      {useSplit ? (
        <View style={styles.splitRoot}>
          <OnboardingBrandRail
            title={'Vendé en el\nlocal. Vendé\nonline.'}
            subtitle="Ahora armamos tu negocio. Tres pasos y entrás a vender."
            steps={welcomeRailSteps()}
            footer="Todo tu negocio, en un solo lugar."
          />
          <View style={styles.splitFormCol}>
            <View style={styles.splitFormInner}>{form}</View>
          </View>
        </View>
      ) : (
        <View style={styles.mobileRoot}>{form}</View>
      )}
    </AuthScreenFrame>
  );
}

const styles = StyleSheet.create({
  splitRoot: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: '#FFFFFF',
  },
  splitFormCol: {
    flex: 1,
    minWidth: 360,
    backgroundColor: '#FFFFFF',
    justifyContent: 'center',
  },
  splitFormInner: {
    paddingVertical: 36,
    paddingHorizontal: 32,
    width: '100%',
    maxWidth: 440,
    alignSelf: 'center',
  },
  mobileRoot: {
    flex: 1,
    justifyContent: 'center',
    paddingHorizontal: 20,
    paddingVertical: spacing.xl,
  },
  form: {
    width: '100%',
    gap: 16,
  },
  formWide: {
    maxWidth: 400,
    alignSelf: 'center',
  },
  formMobile: {
    maxWidth: 400,
    alignSelf: 'center',
  },
  logoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 4,
  },
  logoMark: {
    width: 36,
    height: 36,
    borderRadius: 10,
    backgroundColor: colors.accent,
    alignItems: 'center',
    justifyContent: 'center',
  },
  logoMarkLetter: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '800',
  },
  logoWordmark: {
    fontSize: 18,
    fontWeight: '800',
    letterSpacing: -0.3,
    color: ticketColors.ink,
  },
  head: {
    gap: 8,
  },
  title: {
    fontSize: 22,
    lineHeight: 28,
    letterSpacing: -0.4,
    fontWeight: '800',
    color: ticketColors.ink,
  },
  sub: {
    fontSize: 14,
    lineHeight: 20,
    color: ticketColors.ink2,
  },
  primaryBtn: {
    marginTop: 8,
    minHeight: 48,
    borderRadius: 999,
    backgroundColor: colors.accent,
    paddingHorizontal: spacing.lg,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  primaryLabel: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '700',
  },
  awaitingWrap: {
    alignSelf: 'flex-start',
    marginTop: 4,
  },
  awaitingLink: {
    fontSize: 13,
    fontWeight: '500',
    color: ticketColors.ink3,
  },
});
